import { Fund, MovementFilterNode, Tag } from 'types'

function encodeParam(s: string): string {
    return s
        .replace(/\\/g, '\\\\')
        .replace(/\(/g, '\\(')
        .replace(/\)/g, '\\)')
        .replace(/,/g, '\\,')
}

function decodeParam(s: string): string {
    let result = ''
    let i = 0
    while (i < s.length) {
        if (s[i] === '\\' && i + 1 < s.length) {
            result += s[i + 1]
            i += 2
        } else {
            result += s[i]
            i++
        }
    }
    return result
}

function splitTopLevel(s: string): string[] {
    const parts: string[] = []
    let depth = 0
    let current = ''
    let i = 0
    while (i < s.length) {
        if (s[i] === '\\' && i + 1 < s.length) {
            current += s[i] + s[i + 1]
            i += 2
            continue
        }
        if (s[i] === '(') { depth++; current += s[i] }
        else if (s[i] === ')') { depth--; current += s[i] }
        else if (s[i] === ',' && depth === 0) { parts.push(current); current = '' }
        else { current += s[i] }
        i++
    }
    if (current) parts.push(current)
    return parts
}

export function encodeFilterTree(tree: MovementFilterNode): string {
    if (tree.kind === 'all') return `all(${tree.children.map(encodeFilterTree).join(',')})`
    if (tree.kind === 'any') return `any(${tree.children.map(encodeFilterTree).join(',')})`
    if (tree.kind === 'not') return `not(${encodeFilterTree(tree.child)})`
    const p = encodeParam(tree.parameter)
    switch (tree.type) {
        case 'TEXT':                return `text(${p})`
        case 'HAS_TAG':             return `tag(${p})`
        case 'HAS_NO_TAGS':         return `notag()`
        case 'FUND':                return `fund(${p})`
        case 'DATE_AFTER':          return `from(${p})`
        case 'DATE_BEFORE':         return `until(${p})`
        case 'AMOUNT_LESS_THAN':    return `amount(<${p})`
        case 'AMOUNT_GREATER_THAN': return `amount(>${p})`
        case 'AMOUNT_EQUAL_TO':     return `amount(=${p})`
    }
}

function parseNode(s: string): MovementFilterNode {
    s = s.trim()
    if (s.startsWith('all(') && s.endsWith(')')) {
        const inner = s.slice(4, -1)
        return { kind: 'all', children: inner ? splitTopLevel(inner).map(parseNode) : [] }
    }
    if (s.startsWith('any(') && s.endsWith(')')) {
        const inner = s.slice(4, -1)
        return { kind: 'any', children: inner ? splitTopLevel(inner).map(parseNode) : [] }
    }
    if (s.startsWith('not(') && s.endsWith(')')) {
        const inner = s.slice(4, -1)
        return { kind: 'not', child: parseNode(inner) }
    }
    const match = s.match(/^(\w+)\((.*)\)$/)
    if (!match) throw new Error(`Invalid filter node: ${s}`)
    const fn = match[1]
    const param = decodeParam(match[2] ?? '')
    switch (fn) {
        case 'text':   return { kind: 'leaf', type: 'TEXT',         parameter: param }
        case 'tag':    return { kind: 'leaf', type: 'HAS_TAG',      parameter: param }
        case 'notag':  return { kind: 'leaf', type: 'HAS_NO_TAGS',  parameter: '' }
        case 'fund':   return { kind: 'leaf', type: 'FUND',         parameter: param }
        case 'from':   return { kind: 'leaf', type: 'DATE_AFTER',   parameter: param }
        case 'until':  return { kind: 'leaf', type: 'DATE_BEFORE',  parameter: param }
        case 'amount':
            if (param.startsWith('<')) return { kind: 'leaf', type: 'AMOUNT_LESS_THAN',    parameter: param.slice(1) }
            if (param.startsWith('>')) return { kind: 'leaf', type: 'AMOUNT_GREATER_THAN', parameter: param.slice(1) }
            if (param.startsWith('=')) return { kind: 'leaf', type: 'AMOUNT_EQUAL_TO',     parameter: param.slice(1) }
            throw new Error(`Invalid amount operator: ${param}`)
        default: throw new Error(`Unknown filter function: ${fn}`)
    }
}

export function decodeFilterTree(value: string): MovementFilterNode | null {
    try { return parseNode(value) } catch { return null }
}

export function filterTreeToString(
    node: MovementFilterNode,
    funds: Record<number, Fund> | null,
    tags: Record<number, Tag> | null
): string {
    if (node.kind === 'leaf') {
        switch (node.type) {
            case 'FUND':                return `Fund: ${funds?.[Number(node.parameter)]?.name ?? node.parameter}`
            case 'HAS_TAG':             return `Tag: ${tags?.[Number(node.parameter)]?.name ?? node.parameter}`
            case 'HAS_NO_TAGS':         return 'No tags'
            case 'TEXT':                return `Text: ${node.parameter}`
            case 'DATE_AFTER':          return `From: ${node.parameter}`
            case 'DATE_BEFORE':         return `Until: ${node.parameter}`
            case 'AMOUNT_GREATER_THAN': return `Amount > ${node.parameter}`
            case 'AMOUNT_LESS_THAN':    return `Amount < ${node.parameter}`
            case 'AMOUNT_EQUAL_TO':     return `Amount = ${node.parameter}`
            default:                    return (node as any).type
        }
    }
    if (node.kind === 'not') return `NOT (${filterTreeToString(node.child, funds, tags)})`
    const joinWord = node.kind === 'all' ? ' AND ' : ' OR '
    const parts = node.children.map(c => filterTreeToString(c, funds, tags))
    if (parts.length === 1) return parts[0]
    return `(${parts.join(joinWord)})`
}
